import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Thumbs } from 'swiper';

import ArrowIcon from '@/assets/svg/detailed-car/arrow.svg';
import {
  BottomSliderProps,
  DetailedCarGalleryImageItem,
} from '@/components/DetailedCarGallery/DetailedCarGallery.types';
import Picture from '@/components/Picture';
import { colors } from '@/constants/theme';

SwiperCore.use([Navigation, Thumbs]);

function BottomSlider({
  images,
  setSlider,
  subneilsPerView,
  currentSlideIndex,
}: BottomSliderProps) {
  const prevNavButtonRef = useRef<HTMLDivElement>(null);
  const nextNavButtonRef = useRef<HTMLDivElement>(null);
  const isLooper = subneilsPerView < images.length;
  return (
    <Component subneilsPerView={subneilsPerView}>
      <Swiper
        slidesPerView={subneilsPerView}
        slidesPerGroup={1}
        spaceBetween={5}
        onSwiper={setSlider}
        loop={isLooper}
        allowTouchMove={false}
        navigation={{
          prevEl: prevNavButtonRef.current,
          nextEl: nextNavButtonRef.current,
        }}
      >
        {images.map((item: DetailedCarGalleryImageItem, index: number) => {
          function isActive() {
            if (currentSlideIndex > images.length) {
              return 1 === index + 1;
            } else if (currentSlideIndex < 1) {
              return images.length === index + 1;
            } else {
              return currentSlideIndex === index + 1;
            }
          }
          return (
            <SwiperSlide key={index}>
              <StyledPicture
                mobileSmall={{ src: item.desktop }}
                tabletSmall={{ src: item.desktop }}
                isActive={isActive()}
                loading="lazy"
              />
            </SwiperSlide>
          );
        })}
        {isLooper && (
          <>
            <StyledNavButtonWrapper ref={prevNavButtonRef} prev>
              <ArrowIcon />
            </StyledNavButtonWrapper>

            <StyledNavButtonWrapper ref={nextNavButtonRef} next>
              <ArrowIcon />
            </StyledNavButtonWrapper>
          </>
        )}
      </Swiper>
    </Component>
  );
}

export const Component = styled.div<{
  subneilsPerView: number;
}>`
  margin-top: 20px;
  background-color: inherit;
  border-radius: 8px;
  position: relative;

  .swiper-container {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
  }
  .swiper-slide {
    border-radius: 5px;
    cursor: pointer;
    &:before {
      border-radius: 5px;
      z-index: 2;
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
  ${({ subneilsPerView }) =>
    subneilsPerView >= 6
      ? css`
          padding-top: 9%;
        `
      : subneilsPerView === 5
      ? css`
          padding-top: 11%;
        `
      : subneilsPerView === 4
      ? css`
          padding-top: 15%;
        `
      : subneilsPerView === 3
      ? css`
          padding-top: 19%;
        `
      : subneilsPerView === 2
      ? css`
          padding-top: 25%;
        `
      : css`
          padding-top: 0;
        `}
`;

export const StyledNavButtonWrapper = styled.div<{
  next?: boolean;
  prev?: boolean;
}>`
  position: absolute;
  display: flex;
  align-items: center;
  top: 0;
  bottom: 0;
  border-radius: 0 5px 5px 0;
  padding: 0 2%;
  z-index: 5;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  :hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
  svg {
    path {
      fill: ${colors.white};
    }
  }
  ${({ next }) =>
    next &&
    css`
      right: 0;
    `}
  ${({ prev }) =>
    prev &&
    css`
      left: 0;
      transform: rotate(180deg);
    `}
`;

export const StyledPicture = styled(Picture)<{ isActive: boolean }>`
  position: static !important;

  img,
  picture {
    z-index: 2;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    filter: brightness(0.6);
    background: rgba(0, 0, 0, 0.5);
  }

  img {
    object-fit: cover;
  }
  ${({ isActive }) =>
    isActive &&
    css`
      img,
      picture {
        filter: brightness(1);
        background: rgba(0, 0, 0, 0.1);
      }
    `}
`;

export default BottomSlider;
